import { useCallback, useEffect, useRef, useState } from 'react';
import { buildPayload, reconcileDrafts, sameOrder } from './order';
const EMPTY = [];

export default function usePhotoOrder(propertyId, resources, persist, refresh) {
  const data = resources ?? EMPTY;
  const [drafts, setDrafts] = useState(() => reconcileDrafts({}, data));
  const current = useRef(drafts);
  const latestData = useRef(data);
  latestData.current = data;
  const pending = useRef(null);
  const [savingType, setSavingType] = useState(null);
  const [message, setMessage] = useState('');
  const failed = useRef(null);
  const [failedType, setFailedType] = useState(null);
  const failType = type => { failed.current = type; setFailedType(type); };
  const previousProperty = useRef(propertyId);

  const update = useCallback(fn => {
    const next = fn(current.current);
    current.current = next;
    setDrafts(next);
  }, []);
  useEffect(() => {
    const previous = previousProperty.current === propertyId ? current.current : {};
    previousProperty.current = propertyId;
    const next = reconcileDrafts(previous, data);
    if (JSON.stringify(next) !== JSON.stringify(current.current)) update(() => next);
  }, [propertyId, data, update]);

  const getDirtyTypes = () => Object.keys(current.current).map(Number).filter(type => !sameOrder(current.current[type].working, current.current[type].saved));
  const dirtyTypes = getDirtyTypes();
  const busy = savingType !== null;
  useEffect(() => {
    if (!dirtyTypes.length && !busy) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirtyTypes.length, busy]);
  useEffect(() => {
    if (!message.startsWith('Orden guardado')) return;
    const timer = setTimeout(() => setMessage(''), 3500);
    return () => clearTimeout(timer);
  }, [message]);

  function change(type, ids) {
    if (pending.current || !current.current[type] || current.current[type].stale) return;
    update(old => ({ ...old, [type]: { ...old[type], working: [...ids] } }));
    setMessage('');
  }
  function discardAll() {
    if (pending.current) return;
    update(() => reconcileDrafts({}, latestData.current));
    setMessage('');
  }
  function discard(type) {
    if (pending.current) return;
    const server = reconcileDrafts({}, latestData.current);
    update(old => ({ ...old, [type]: server[type] }));
    setMessage('');
  }
  function save(type) {
    if (pending.current) return Promise.resolve(false);
    const draft = current.current[type];
    if (draft.stale) {
      failType(type);
      setMessage('La lista de recursos cambió. Descarta el borrador y recarga antes de guardar.');
      return Promise.resolve(false);
    }
    if (sameOrder(draft.saved, draft.working)) return Promise.resolve(true);
    const snapshot = [...draft.working];
    setSavingType(type);
    setMessage('Guardando…');
    const task = (async () => {
      try {
        await persist(buildPayload(propertyId, type, snapshot));
        update(old => ({ ...old, [type]: { working: snapshot, saved: snapshot, stale: false } }));
        failType(null);
        setMessage('Orden guardado.');
        if (refresh) {
          try { await refresh(); } catch (_) { setMessage('Orden guardado. No se pudo actualizar la vista; recarga para verificarlo.'); }
        }
        return true;
      } catch (error) {
        if (error?.response?.status === 422) update(old => ({ ...old, [type]: { ...old[type], stale: true } }));
        failType(type);
        setMessage(`No se pudo guardar el orden de ${['Fotos', 'Fotos 360', 'Videos', 'Tour Virtual'][type - 1]}. Tus cambios siguen aquí, sin guardar.`);
        return false;
      } finally {
        pending.current = null;
        setSavingType(null);
      }
    })();
    pending.current = task;
    return task;
  }
  async function saveAll() {
    if (pending.current && !(await pending.current)) return false;
    for (const type of getDirtyTypes()) if (!(await save(type))) return false;
    return true;
  }
  return { failedType, getFailedType: () => failed.current, drafts, dirtyTypes, getDirtyTypes, busy, savingType, message, change, discard, discardAll, save, saveAll, waitForSave: () => pending.current ?? Promise.resolve(true) };
}
